/**
 * Company Settings Page - Informations Générales + Logo
 */

import React from 'react';
import { useTranslation } from 'react-i18next';
import { Save, Building2, ImageIcon } from 'lucide-react';
import { Button, Spinner } from '@/components/ui-v2';
import { useCompanySettings } from '@/hooks/useCompanySettings';
import { useCompanyLogo } from '@/hooks/useCompanyLogo';
import { CompanyTradeSelect } from '@/components/shared/CompanyTradeSelect';
import { getAssetUrl } from '@/utils/api';
import type { BusinessType } from '@/types/models';

const cardClasses = `bg-white/20 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-300/10
  border border-white/30 p-6 hover:bg-white/70 hover:shadow-2xl
  transition-all duration-300`;

const inputClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
  focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
  hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
  transition-all duration-300 outline-none text-sm`;

const selectClasses = `w-full px-4 py-3 bg-white/40 border border-white/50 rounded-2xl
  focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:bg-white
  hover:bg-white hover:border-teal-400 hover:shadow-lg hover:shadow-teal-100/50
  transition-all duration-300 outline-none cursor-pointer text-sm`;

export const CompanySettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const {
    company,
    loading,
    saving,
    error,
    updateField,
    saveCompany,
  } = useCompanySettings();

  const {
    uploading,
    previewUrl,
    uploadLogo,
    handleFileSelect,
    clearPreview,
  } = useCompanyLogo(company?.id);

  const handleSave = async () => {
    await saveCompany();
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const isValid = handleFileSelect(file);
    if (!isValid) return;
    await uploadLogo(file);
  };

  const handleLogoUrlChange = (url: string) => {
    updateField('logoUrl', url);
    clearPreview();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error || !company) {
    return (
      <div className={cardClasses}>
        <div className="text-center py-8">
          <div className="p-4 bg-red-100 rounded-2xl w-fit mx-auto mb-4">
            <Building2 className="w-12 h-12 text-red-500" />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {error || t('settings.company.noCompany')}
          </h3>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header avec bouton save */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-teal-100 rounded-xl">
            <Building2 className="w-5 h-5 text-teal-600" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">{t('settings.company.title')}</h2>
        </div>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="secondary"
          icon={<Save className="w-4 h-4" />}
        >
          {saving ? t('common.actions.saving') : t('common.actions.save')}
        </Button>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* Informations générales */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="p-2 bg-teal-100 rounded-xl">
              <Building2 className="w-5 h-5 text-teal-600" />
            </div>
            {t('settings.company.generalInfo')}
          </h3>
          <div className="grid grid-cols-1 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.company.name')}
              </label>
              <input
                type="text"
                value={company.legalName || ""}
                onChange={(e) => updateField("legalName", e.target.value)}
                className={inputClasses}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('auth.fields.email')}
              </label>
              <input
                type="email"
                value={company.email || ""}
                onChange={(e) => updateField("email", e.target.value)}
                className={inputClasses}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.company.phone')}
              </label>
              <input
                type="text"
                value={company.phone || ""}
                onChange={(e) => updateField("phone", e.target.value)}
                className={inputClasses}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.company.siret')}
              </label>
              <input
                type="text"
                value={company.registrationNumber || ""}
                onChange={(e) => updateField("registrationNumber", e.target.value)}
                className={inputClasses}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.company.vatNumber')}
              </label>
              <input
                type="text"
                value={company.vatNumber || ""}
                onChange={(e) => updateField("vatNumber", e.target.value)}
                className={inputClasses}
                placeholder="FR87 523 431 328"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.company.address')}
              </label>
              <input
                type="text"
                value={company.addressLine1 || ""}
                onChange={(e) => updateField("addressLine1", e.target.value)}
                className={inputClasses}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.company.zipCode')}
                </label>
                <input
                  type="text"
                  value={company.zipCode || ""}
                  onChange={(e) => updateField("zipCode", e.target.value)}
                  className={inputClasses}
                  placeholder="75011"
                  maxLength={5}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.company.city')}
                </label>
                <input
                  type="text"
                  value={company.city || ""}
                  onChange={(e) => updateField("city", e.target.value)}
                  className={inputClasses}
                  placeholder={t('settings.company.cityPlaceholder')}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.company.country')}
              </label>
              <select
                value={company.countryCode || "FR"}
                onChange={(e) => updateField("countryCode", e.target.value)}
                className={selectClasses}
              >
                <option value="FR">{t('settings.company.countries.FR')}</option>
                <option value="BE">{t('settings.company.countries.BE')}</option>
                <option value="CH">{t('settings.company.countries.CH')}</option>
                <option value="LU">{t('settings.company.countries.LU')}</option>
                <option value="DE">{t('settings.company.countries.DE')}</option>
                <option value="ES">{t('settings.company.countries.ES')}</option>
                <option value="IT">{t('settings.company.countries.IT')}</option>
                <option value="PT">{t('settings.company.countries.PT')}</option>
                <option value="NL">{t('settings.company.countries.NL')}</option>
              </select>
            </div>

            {/* Type d'activité */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('settings.company.businessType')}
              </label>
              <select
                value={company.businessType || 'SERVICE'}
                onChange={(e) => updateField('businessType', e.target.value)}
                className={selectClasses}
              >
                {(['SERVICE', 'COMMERCE', 'ARTISAN_BTP', 'PME_BTP', 'LIBERAL', 'MIXTE'] as const).map((bt) => (
                  <option key={bt} value={bt}>
                    {t(`settings.company.businessTypes.${bt}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Métier */}
            <CompanyTradeSelect
              value={company.companyTrade ?? ''}
              onChange={(tradeCode, tradeBt) => {
                updateField('companyTrade', tradeCode);
                if (tradeBt) {
                  updateField('businessType', tradeBt);
                }
              }}
              selectClasses={selectClasses}
            />
          </div>
        </div>

        {/* Logo */}
        <div className={cardClasses}>
          <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <div className="p-2 bg-teal-100 rounded-xl">
              <ImageIcon className="w-5 h-5 text-teal-600" />
            </div>
            {t('settings.company.logo')}
          </h3>
          <div className="flex flex-col items-start gap-6">
            {/* Prévisualisation */}
            <div className="w-full">
              <div className="w-full h-48 border-2 border-dashed border-white/50 rounded-2xl flex items-center justify-center bg-white/30">
                {(previewUrl || company.logoUrl) ? (
                  <img
                    src={getAssetUrl(previewUrl || company.logoUrl || '')}
                    alt={t('settings.company.logo')}
                    className="max-w-full max-h-full object-contain p-2"
                  />
                ) : (
                  <div className="text-center text-gray-400 p-4">
                    <ImageIcon className="w-12 h-12 mx-auto mb-2" />
                    <p className="text-sm">{t('settings.company.noLogo')}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Upload et URL */}
            <div className="flex-1 space-y-4 w-full">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.company.uploadLogo')}
                </label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                  <label className={`
                    inline-flex items-center justify-center gap-2 cursor-pointer
                    bg-gradient-to-r from-teal-600 to-teal-700 text-white
                    hover:from-teal-700 hover:to-teal-800 hover:shadow-2xl
                    shadow-lg shadow-teal-500/30
                    px-8 py-3.5 rounded-2xl font-bold
                    transition-all duration-300 hover:-translate-y-0.5
                    ${uploading ? 'opacity-50 cursor-not-allowed' : ''}
                  `}>
                    {uploading ? t('common.actions.uploading') : t('settings.company.chooseFile')}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                  {uploading && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Spinner size="sm" />
                      {t('common.actions.uploading')}
                    </div>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  {t('settings.company.logoFormats')}
                </p>
              </div>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/50"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-transparent text-gray-500">{t('common.or')}</span>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.company.logoUrl')}
                </label>
                <input
                  type="text"
                  value={company.logoUrl || ""}
                  onChange={(e) => handleLogoUrlChange(e.target.value)}
                  className={inputClasses}
                  placeholder={t('settings.company.logoUrlPlaceholder')}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettingsPage;
